import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import jsPDF from 'jspdf'
import { saveAs } from 'file-saver'
import { supabase } from '../supabaseClient'
import { enviarProcuracaoParaClicksign } from '../services/clicksignProcuracao'
import './DocumentosJuridicosModal.css'

const REGIMES = [
  'Comunhão Parcial de Bens',
  'Comunhão Universal de Bens',
  'Separação Total de Bens'
]

const soDigitos = (s) => String(s || '').replace(/\D/g, '')

const formatCpf = (s) => {
  const d = soDigitos(s).slice(0, 11)
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

const formatCep = (s) => {
  const d = soDigitos(s).slice(0, 8)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

const formatWhatsapp = (s) => {
  const d = soDigitos(s).slice(0, 11)
  if (d.length === 0) return ''
  if (d.length <= 2) return `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

const formatRg = (s) => {
  const d = String(s || '').replace(/[^\dXx]/gi, '').slice(0, 9).toUpperCase()
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})([\dX])$/, '$1-$2')
}

const formatMoeda = (s) => {
  const numbers = soDigitos(s)
  if (!numbers) return ''
  const amount = parseFloat(numbers) / 100
  return amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatUf = (s) => String(s || '').replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2)

const formatDateBR = (iso) => {
  if (!iso) return ''
  const parts = String(iso).split('-')
  if (parts.length !== 3) return iso
  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

const dataAtualExtenso = () =>
  new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })

const formatarEndereco = ({ logradouro, numero, bairro, cidade, uf }) => {
  const log = String(logradouro || '').trim()
  const num = String(numero || '').trim()
  const bai = String(bairro || '').trim()
  const cid = String(cidade || '').trim()
  const estado = String(uf || '').trim()
  const partes = []
  if (log) partes.push(num ? `${log}, nº ${num}` : log)
  else if (num) partes.push(`nº ${num}`)
  if (bai) partes.push(`Bairro ${bai}`)
  if (cid && estado) partes.push(`${cid} - ${estado}`)
  else if (cid) partes.push(cid)
  else if (estado) partes.push(estado)
  return partes.join(', ') || '[Endereço completo]'
}

const uniaoVazio = (cliente = {}) => ({
  u1_nome: cliente.nome_completo || '',
  u1_nacionalidade: 'Brasileiro(a)',
  u1_estadocivil: 'Solteiro(a)',
  u1_profissao: '',
  u1_cpf: formatCpf(cliente.cpf),
  u1_rg: formatRg(cliente.rg),
  u1_cep: formatCep(cliente.cep),
  u1_logradouro: cliente.logradouro || '',
  u1_numero: cliente.numero || '',
  u1_bairro: cliente.bairro || '',
  u1_cidade: cliente.cidade || '',
  u1_uf: formatUf(cliente.estado),
  u1_telefone: formatWhatsapp(cliente.telefone),
  u2_nome: '',
  u2_nacionalidade: 'Brasileiro(a)',
  u2_estadocivil: 'Solteiro(a)',
  u2_profissao: '',
  u2_cpf: '',
  u2_rg: '',
  u2_telefone: '',
  u_data_inicio: '',
  u_regime: REGIMES[0]
})

const aluguelVazio = (cliente = {}) => ({
  locador_nome: '',
  locador_doc: '',
  locador_telefone: '',
  locatario_nome: cliente.nome_completo || '',
  locatario_cpf: formatCpf(cliente.cpf),
  locatario_telefone: formatWhatsapp(cliente.telefone),
  cep: formatCep(cliente.cep),
  logradouro: cliente.logradouro || '',
  numero: cliente.numero || '',
  bairro: cliente.bairro || '',
  cidade: cliente.cidade || '',
  uf: formatUf(cliente.estado),
  data_inicio: '',
  data_fim: '',
  valor: ''
})

function montarTextosUniao(f) {
  const endereco = formatarEndereco({
    logradouro: f.u1_logradouro,
    numero: f.u1_numero,
    bairro: f.u1_bairro,
    cidade: f.u1_cidade,
    uf: f.u1_uf
  })
  const nome1 = f.u1_nome.trim() || '[Nome do Companheiro 1]'
  const nome2 = f.u2_nome.trim() || '[Nome do Companheiro 2]'
  return {
    titulo: 'DECLARAÇÃO PARTICULAR DE UNIÃO ESTÁVEL',
    paragrafos: [
      'Por este instrumento particular, os declarantes abaixo qualificados:',
      `1. PRIMEIRO(A) DECLARANTE: ${nome1}, ${f.u1_nacionalidade || 'brasileiro(a)'}, ${f.u1_estadocivil || 'solteiro(a)'}, ${f.u1_profissao || '[Profissão]'}, portador(a) do CPF nº ${f.u1_cpf || '[CPF]'} e RG nº ${f.u1_rg || '[RG]'}, residente e domiciliado(a) na ${endereco}.`,
      `2. SEGUNDO(A) DECLARANTE: ${nome2}, ${f.u2_nacionalidade || 'brasileiro(a)'}, ${f.u2_estadocivil || 'solteiro(a)'}, ${f.u2_profissao || '[Profissão]'}, portador(a) do CPF nº ${f.u2_cpf || '[CPF]'} e RG nº ${f.u2_rg || '[RG]'}.`,
      'Têm entre si justo e acordado declarar para todos os fins de direito que mantêm convívio público, contínuo e duradouro, estabelecido com o objetivo de constituição de família, caracterizando assim UNIÃO ESTÁVEL, nos termos do art. 1.723 e seguintes do Código Civil Brasileiro.',
      `Declaram ainda que a convivência teve início em ${f.u_data_inicio ? formatDateBR(f.u_data_inicio) : '[Data de Início]'}, regendo-se a relação patrimonial, no que couber, pelo regime de ${f.u_regime || REGIMES[0]}.`,
      'Por ser expressão da verdade, firmam a presente declaração.'
    ],
    localData: `Local e Data: ${dataAtualExtenso()}.`,
    assinaturas: [nome1, nome2]
  }
}

function montarTextosAluguel(f) {
  const endereco = formatarEndereco({
    logradouro: f.logradouro,
    numero: f.numero,
    bairro: f.bairro,
    cidade: f.cidade,
    uf: f.uf
  })
  const locador = f.locador_nome.trim() || '[Nome do Locador]'
  const locatario = f.locatario_nome.trim() || '[Nome do Locatário]'
  return {
    titulo: 'CONTRATO PARTICULAR DE LOCAÇÃO RESIDENCIAL',
    paragrafos: [
      `LOCADOR(A): ${locador}, inscrito(a) no CPF/CNPJ sob nº ${f.locador_doc || '[CPF/CNPJ]'}.`,
      `LOCATÁRIO(A): ${locatario}, inscrito(a) no CPF sob nº ${f.locatario_cpf || '[CPF]'}.`,
      `As partes acima qualificadas celebram pelo presente instrumento particular o presente contrato de locação residencial do imóvel situado na ${endereco}.`,
      `CLÁUSULA PRIMEIRA – DO OBJETO E PRAZO: O presente contrato tem por objeto a locação do imóvel residencial acima descrito. O prazo de vigência da locação terá início em ${f.data_inicio ? formatDateBR(f.data_inicio) : '[Data Início]'} e término em ${f.data_fim ? formatDateBR(f.data_fim) : '[Data Fim]'}, data em que o Locatário deverá restituir o imóvel ao Locador livre de pessoas e coisas.`,
      `CLÁUSULA SEGUNDA – DO VALOR E FORMA DE PAGAMENTO: O aluguel mensal é estipulado em R$ ${f.valor || '0,00'}, a ser pago pontualmente até o dia 10 (dez) de cada mês subsequente ao vencido.`,
      'E por estarem justos e contratados, assinam o presente instrumento em duas vias de igual teor.'
    ],
    localData: `Local e Data: ${dataAtualExtenso()}.`,
    assinaturas: [locador, locatario]
  }
}

function gerarPdfDocumento({ titulo, paragrafos, localData, assinaturas }) {
  const pdf = new jsPDF('p', 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const margin = 20
  const maxWidth = pageWidth - margin * 2
  let y = 28

  pdf.setFont('times', 'bold')
  pdf.setFontSize(14)
  const tituloLinhas = pdf.splitTextToSize(titulo, maxWidth)
  pdf.text(tituloLinhas, pageWidth / 2, y, { align: 'center' })
  y += tituloLinhas.length * 7 + 10

  pdf.setFont('times', 'normal')
  pdf.setFontSize(12)
  paragrafos.forEach((p) => {
    const linhas = pdf.splitTextToSize(p, maxWidth)
    if (y + linhas.length * 6.5 > 270) {
      pdf.addPage()
      y = 28
    }
    pdf.text(linhas, margin, y)
    y += linhas.length * 6.5 + 5
  })

  y += 8
  pdf.setFontSize(11)
  pdf.text(localData, pageWidth / 2, y, { align: 'center' })
  y += 28

  const colW = (pageWidth - margin * 2 - 12) / 2
  assinaturas.forEach((nome, i) => {
    const x = i === 0 ? margin : margin + colW + 12
    if (y > 270) {
      pdf.addPage()
      y = 40
    }
    pdf.setDrawColor(80)
    pdf.line(x, y, x + colW, y)
    pdf.setFontSize(10)
    pdf.text(nome, x + colW / 2, y + 6, { align: 'center' })
  })

  return pdf
}

async function buscarCepApi(cep) {
  const limpo = soDigitos(cep)
  if (limpo.length !== 8) throw new Error('CEP inválido. Digite 8 dígitos.')
  const resp = await fetch(`https://viacep.com.br/ws/${limpo}/json/`)
  const data = await resp.json()
  if (data.erro) throw new Error('CEP não encontrado.')
  return {
    logradouro: data.logradouro || '',
    bairro: data.bairro || '',
    cidade: data.localidade || '',
    uf: data.uf || ''
  }
}

const Campo = ({ label, children }) => (
  <label className="doc-jur-campo">
    <span>{label}</span>
    {children}
  </label>
)

const DocumentosJuridicosModal = ({
  open,
  cliente,
  onClose,
  canEnviarClicksign,
  onNotify
}) => {
  const [aba, setAba] = useState('uniao')
  const [uniao, setUniao] = useState(() => uniaoVazio(cliente || {}))
  const [aluguel, setAluguel] = useState(() => aluguelVazio(cliente || {}))
  const [buscandoCep, setBuscandoCep] = useState('')
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  useEffect(() => {
    if (!open) return
    setAba('uniao')
    setUniao(uniaoVazio(cliente || {}))
    setAluguel(aluguelVazio(cliente || {}))
    setMsg({ text: '', type: '' })
    setResultado(null)
    setEnviando(false)
  }, [open, cliente])

  const setU = (campo, valor) => setUniao((prev) => ({ ...prev, [campo]: valor }))
  const setA = (campo, valor) => setAluguel((prev) => ({ ...prev, [campo]: valor }))

  const textosAtuais = () => (aba === 'uniao' ? montarTextosUniao(uniao) : montarTextosAluguel(aluguel))

  const avisar = (text, type = 'error') => {
    setMsg({ text, type })
    onNotify?.(text, type)
  }

  const handleBuscarCep = async (destino) => {
    const cep = destino === 'uniao' ? uniao.u1_cep : aluguel.cep
    setBuscandoCep(destino)
    try {
      const end = await buscarCepApi(cep)
      if (destino === 'uniao') {
        setUniao((prev) => ({
          ...prev,
          u1_logradouro: end.logradouro,
          u1_bairro: end.bairro,
          u1_cidade: end.cidade,
          u1_uf: end.uf
        }))
      } else {
        setAluguel((prev) => ({
          ...prev,
          logradouro: end.logradouro,
          bairro: end.bairro,
          cidade: end.cidade,
          uf: end.uf
        }))
      }
      avisar('Endereço carregado. Confira o número.', 'success')
    } catch (err) {
      avisar(err.message || 'Erro ao consultar o CEP.')
    } finally {
      setBuscandoCep('')
    }
  }

  const nomeArquivo = () => {
    const base = aba === 'uniao' ? 'Uniao_Estavel' : 'Contrato_Aluguel'
    const nome = (cliente?.nome_completo || 'Cliente').replace(/\s+/g, '_')
    return `${base}_${nome}_${new Date().toISOString().split('T')[0]}.pdf`
  }

  const handleBaixarPdf = () => {
    if (aba === 'uniao' && !uniao.u1_nome.trim()) {
      avisar('Informe o nome do primeiro declarante.')
      return
    }
    if (aba === 'aluguel' && (!aluguel.locador_nome.trim() || !aluguel.locatario_nome.trim())) {
      avisar('Informe o locador e o locatário.')
      return
    }
    const pdf = gerarPdfDocumento(textosAtuais())
    pdf.save(nomeArquivo())
    avisar('PDF gerado.', 'success')
  }

  const montarSignatarios = () => {
    if (aba === 'uniao') {
      const s1 = { name: uniao.u1_nome.trim(), documentation: soDigitos(uniao.u1_cpf), phone_number: soDigitos(uniao.u1_telefone) }
      const s2 = { name: uniao.u2_nome.trim(), documentation: soDigitos(uniao.u2_cpf), phone_number: soDigitos(uniao.u2_telefone) }
      return [s1, s2]
    }
    return [
      { name: aluguel.locador_nome.trim(), documentation: soDigitos(aluguel.locador_doc), phone_number: soDigitos(aluguel.locador_telefone) },
      { name: aluguel.locatario_nome.trim(), documentation: soDigitos(aluguel.locatario_cpf), phone_number: soDigitos(aluguel.locatario_telefone) }
    ]
  }

  const handleEnviarClicksign = async () => {
    const signers = montarSignatarios()
    for (const s of signers) {
      if (!s.name) {
        avisar('Informe o nome de todos os signatários.')
        return
      }
      if (s.documentation.length !== 11) {
        avisar(`CPF inválido para "${s.name}". A Clicksign exige CPF com 11 dígitos.`)
        return
      }
      if (s.phone_number.length < 10 || s.phone_number.length > 11) {
        avisar(`Informe o WhatsApp de "${s.name}" (DDD + número).`)
        return
      }
    }
    setEnviando(true)
    setResultado(null)
    try {
      const pdf = gerarPdfDocumento(textosAtuais())
      const bytes = pdf.output('arraybuffer')
      const bucket = 'clicksign-uploads'
      const storagePath = `temp/${Date.now()}-${Math.random().toString(36).slice(2, 11)}.pdf`
      const { error: uploadErr } = await supabase.storage.from(bucket).upload(
        storagePath,
        new Blob([bytes], { type: 'application/pdf' }),
        { contentType: 'application/pdf', upsert: false }
      )
      if (uploadErr) {
        avisar('Falha ao enviar o PDF. Verifique o bucket clicksign-uploads.')
        return
      }
      const result = await enviarProcuracaoParaClicksign({
        storage_path: storagePath,
        storage_bucket: bucket,
        filename: nomeArquivo(),
        send_via: 'whatsapp',
        signers,
        notification_sender_name: 'ConectSolar'
      })
      if (!result.success) {
        avisar(result.error || 'Falha ao enviar para a Clicksign.')
        return
      }
      setResultado({ url: result.signing_url, message: result.message })
      avisar(result.message || 'Documento enviado para assinatura pelo WhatsApp.', 'success')
    } catch (err) {
      avisar(err.message || 'Erro ao enviar para a Clicksign.')
    } finally {
      setEnviando(false)
    }
  }

  if (!open || !cliente) return null

  return createPortal(
    <div className="modal-overlay doc-jur-overlay" onClick={() => !enviando && onClose()}>
      <div className="doc-jur-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-documentos-header">
          <h2>Contrato de aluguel e união estável</h2>
          <button type="button" className="btn-close-modal" onClick={onClose} disabled={enviando} title="Fechar">✕</button>
        </div>

        <div className="doc-jur-body">
          <p className="modal-documentos-cliente"><strong>Cliente:</strong> {cliente.nome_completo}</p>

          <div className="doc-jur-tabs">
            <button
              type="button"
              className={`doc-jur-tab${aba === 'uniao' ? ' is-active' : ''}`}
              onClick={() => setAba('uniao')}
            >
              União estável
            </button>
            <button
              type="button"
              className={`doc-jur-tab doc-jur-tab--aluguel${aba === 'aluguel' ? ' is-active' : ''}`}
              onClick={() => setAba('aluguel')}
            >
              Contrato de aluguel
            </button>
          </div>

          {msg.text && (
            <div className={`doc-jur-toast doc-jur-toast--${msg.type === 'success' ? 'ok' : 'erro'}`}>
              {msg.text}
            </div>
          )}

          <div className="doc-jur-form">
              {aba === 'uniao' ? (
                <>
                  <div className="doc-jur-bloco">
                    <h3>Companheiro(a) 01</h3>
                    <Campo label="Nome completo">
                      <input className="form-control" value={uniao.u1_nome} onChange={(e) => setU('u1_nome', e.target.value)} />
                    </Campo>
                    <div className="doc-jur-dupla">
                      <Campo label="Nacionalidade">
                        <input className="form-control" value={uniao.u1_nacionalidade} onChange={(e) => setU('u1_nacionalidade', e.target.value)} />
                      </Campo>
                      <Campo label="Estado civil">
                        <input className="form-control" value={uniao.u1_estadocivil} onChange={(e) => setU('u1_estadocivil', e.target.value)} />
                      </Campo>
                    </div>
                    <div className="doc-jur-dupla">
                      <Campo label="Profissão">
                        <input className="form-control" value={uniao.u1_profissao} onChange={(e) => setU('u1_profissao', e.target.value)} />
                      </Campo>
                      <Campo label="CPF">
                        <input className="form-control" value={uniao.u1_cpf} inputMode="numeric" maxLength={14} placeholder="000.000.000-00" onChange={(e) => setU('u1_cpf', formatCpf(e.target.value))} />
                      </Campo>
                    </div>
                    <div className="doc-jur-dupla">
                      <Campo label="RG">
                        <input className="form-control" value={uniao.u1_rg} maxLength={12} placeholder="00.000.000-0" onChange={(e) => setU('u1_rg', formatRg(e.target.value))} />
                      </Campo>
                      <Campo label="WhatsApp (assinatura)">
                        <input className="form-control" value={uniao.u1_telefone} inputMode="tel" maxLength={15} placeholder="(00) 00000-0000" onChange={(e) => setU('u1_telefone', formatWhatsapp(e.target.value))} />
                      </Campo>
                    </div>
                    <div className="doc-jur-cep">
                      <Campo label="CEP">
                        <input className="form-control" value={uniao.u1_cep} inputMode="numeric" maxLength={9} placeholder="00000-000" onChange={(e) => setU('u1_cep', formatCep(e.target.value))} />
                      </Campo>
                      <button type="button" className="btn btn-sm btn-primary" disabled={buscandoCep === 'uniao'} onClick={() => handleBuscarCep('uniao')}>
                        {buscandoCep === 'uniao' ? 'Buscando…' : 'Buscar CEP'}
                      </button>
                    </div>
                    <Campo label="Logradouro">
                      <input className="form-control" value={uniao.u1_logradouro} onChange={(e) => setU('u1_logradouro', e.target.value)} />
                    </Campo>
                    <div className="doc-jur-trio">
                      <Campo label="Número">
                        <input className="form-control" value={uniao.u1_numero} onChange={(e) => setU('u1_numero', e.target.value)} />
                      </Campo>
                      <Campo label="Bairro">
                        <input className="form-control" value={uniao.u1_bairro} onChange={(e) => setU('u1_bairro', e.target.value)} />
                      </Campo>
                      <Campo label="Cidade">
                        <input className="form-control" value={uniao.u1_cidade} onChange={(e) => setU('u1_cidade', e.target.value)} />
                      </Campo>
                    </div>
                    <Campo label="UF">
                      <input className="form-control" value={uniao.u1_uf} maxLength={2} placeholder="UF" onChange={(e) => setU('u1_uf', formatUf(e.target.value))} />
                    </Campo>
                  </div>

                  <div className="doc-jur-bloco">
                    <h3>Companheiro(a) 02</h3>
                    <Campo label="Nome completo">
                      <input className="form-control" value={uniao.u2_nome} onChange={(e) => setU('u2_nome', e.target.value)} />
                    </Campo>
                    <div className="doc-jur-dupla">
                      <Campo label="Nacionalidade">
                        <input className="form-control" value={uniao.u2_nacionalidade} onChange={(e) => setU('u2_nacionalidade', e.target.value)} />
                      </Campo>
                      <Campo label="Estado civil">
                        <input className="form-control" value={uniao.u2_estadocivil} onChange={(e) => setU('u2_estadocivil', e.target.value)} />
                      </Campo>
                    </div>
                    <div className="doc-jur-dupla">
                      <Campo label="Profissão">
                        <input className="form-control" value={uniao.u2_profissao} onChange={(e) => setU('u2_profissao', e.target.value)} />
                      </Campo>
                      <Campo label="CPF">
                        <input className="form-control" value={uniao.u2_cpf} inputMode="numeric" maxLength={14} placeholder="000.000.000-00" onChange={(e) => setU('u2_cpf', formatCpf(e.target.value))} />
                      </Campo>
                    </div>
                    <div className="doc-jur-dupla">
                      <Campo label="RG">
                        <input className="form-control" value={uniao.u2_rg} maxLength={12} placeholder="00.000.000-0" onChange={(e) => setU('u2_rg', formatRg(e.target.value))} />
                      </Campo>
                      <Campo label="WhatsApp (assinatura)">
                        <input className="form-control" value={uniao.u2_telefone} inputMode="tel" maxLength={15} placeholder="(00) 00000-0000" onChange={(e) => setU('u2_telefone', formatWhatsapp(e.target.value))} />
                      </Campo>
                    </div>
                  </div>

                  <div className="doc-jur-dupla">
                    <Campo label="Data de início da união">
                      <input type="date" className="form-control" value={uniao.u_data_inicio} onChange={(e) => setU('u_data_inicio', e.target.value)} />
                    </Campo>
                    <Campo label="Regime de bens">
                      <select className="form-select" value={uniao.u_regime} onChange={(e) => setU('u_regime', e.target.value)}>
                        {REGIMES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </Campo>
                  </div>
                </>
              ) : (
                <>
                  <div className="doc-jur-bloco doc-jur-bloco--aluguel">
                    <h3>Locador (proprietário)</h3>
                    <Campo label="Nome do locador">
                      <input className="form-control" value={aluguel.locador_nome} onChange={(e) => setA('locador_nome', e.target.value)} />
                    </Campo>
                    <div className="doc-jur-dupla">
                      <Campo label="CPF (11 dígitos para Clicksign)">
                        <input className="form-control" value={aluguel.locador_doc} inputMode="numeric" maxLength={14} placeholder="000.000.000-00" onChange={(e) => setA('locador_doc', formatCpf(e.target.value))} />
                      </Campo>
                      <Campo label="WhatsApp (assinatura)">
                        <input className="form-control" value={aluguel.locador_telefone} inputMode="tel" maxLength={15} placeholder="(00) 00000-0000" onChange={(e) => setA('locador_telefone', formatWhatsapp(e.target.value))} />
                      </Campo>
                    </div>
                  </div>

                  <div className="doc-jur-bloco doc-jur-bloco--aluguel">
                    <h3>Locatário (inquilino)</h3>
                    <Campo label="Nome do locatário">
                      <input className="form-control" value={aluguel.locatario_nome} onChange={(e) => setA('locatario_nome', e.target.value)} />
                    </Campo>
                    <div className="doc-jur-dupla">
                      <Campo label="CPF">
                        <input className="form-control" value={aluguel.locatario_cpf} inputMode="numeric" maxLength={14} placeholder="000.000.000-00" onChange={(e) => setA('locatario_cpf', formatCpf(e.target.value))} />
                      </Campo>
                      <Campo label="WhatsApp (assinatura)">
                        <input className="form-control" value={aluguel.locatario_telefone} inputMode="tel" maxLength={15} placeholder="(00) 00000-0000" onChange={(e) => setA('locatario_telefone', formatWhatsapp(e.target.value))} />
                      </Campo>
                    </div>
                  </div>

                  <div className="doc-jur-bloco doc-jur-bloco--aluguel">
                    <h3>Imóvel locado</h3>
                    <div className="doc-jur-cep">
                      <Campo label="CEP">
                        <input className="form-control" value={aluguel.cep} inputMode="numeric" maxLength={9} placeholder="00000-000" onChange={(e) => setA('cep', formatCep(e.target.value))} />
                      </Campo>
                      <button type="button" className="btn btn-sm btn-warning" disabled={buscandoCep === 'aluguel'} onClick={() => handleBuscarCep('aluguel')}>
                        {buscandoCep === 'aluguel' ? 'Buscando…' : 'Buscar CEP'}
                      </button>
                    </div>
                    <Campo label="Logradouro">
                      <input className="form-control" value={aluguel.logradouro} onChange={(e) => setA('logradouro', e.target.value)} />
                    </Campo>
                    <div className="doc-jur-trio">
                      <Campo label="Número">
                        <input className="form-control" value={aluguel.numero} onChange={(e) => setA('numero', e.target.value)} />
                      </Campo>
                      <Campo label="Bairro">
                        <input className="form-control" value={aluguel.bairro} onChange={(e) => setA('bairro', e.target.value)} />
                      </Campo>
                      <Campo label="Cidade">
                        <input className="form-control" value={aluguel.cidade} onChange={(e) => setA('cidade', e.target.value)} />
                      </Campo>
                    </div>
                    <Campo label="UF">
                      <input className="form-control" value={aluguel.uf} maxLength={2} placeholder="UF" onChange={(e) => setA('uf', formatUf(e.target.value))} />
                    </Campo>
                  </div>

                  <div className="doc-jur-bloco doc-jur-bloco--aluguel">
                    <h3>Vigência e valor</h3>
                    <div className="doc-jur-dupla">
                      <Campo label="Início da locação">
                        <input type="date" className="form-control" value={aluguel.data_inicio} onChange={(e) => setA('data_inicio', e.target.value)} />
                      </Campo>
                      <Campo label="Término da locação">
                        <input type="date" className="form-control" value={aluguel.data_fim} onChange={(e) => setA('data_fim', e.target.value)} />
                      </Campo>
                    </div>
                    <Campo label="Valor do aluguel mensal (R$)">
                      <input className="form-control" value={aluguel.valor} inputMode="numeric" placeholder="0,00" onChange={(e) => setA('valor', formatMoeda(e.target.value))} />
                    </Campo>
                  </div>
                </>
              )}
          </div>

          {resultado?.url && (
            <div className="doc-jur-resultado">
              <p>{resultado.message}</p>
              <a href={resultado.url} target="_blank" rel="noopener noreferrer">Abrir link de assinatura</a>
            </div>
          )}
        </div>

        <div className="modal-documentos-footer doc-jur-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={enviando}>Fechar</button>
          <button type="button" className="btn btn-outline-primary" onClick={handleBaixarPdf} disabled={enviando}>Baixar PDF</button>
          {canEnviarClicksign && (
            <button type="button" className="btn btn-primary" onClick={handleEnviarClicksign} disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar para assinatura'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default DocumentosJuridicosModal
